/**
 * Newsletter Form Web Component
 *
 * A custom element that handles newsletter form submissions to the newsletter app endpoint.
 *
 * Usage:
 * <newsletter-form source="footer" success-message="Thanks!" error-message="Error">
 *   <form>...</form>
 * </newsletter-form>
 */
class NewsletterForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.boundSubmitHandler = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (!this.form) return;

    this.form.addEventListener("submit", this.boundSubmitHandler);
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this.boundSubmitHandler);
    }
  }

  get endpoint() {
    return this.getAttribute("endpoint") || "/apps/forms/newsletter";
  }

  get source() {
    return this.getAttribute("source") || "";
  }

  get successMessage() {
    return this.getAttribute("success-message") || "Thank you for subscribing!";
  }

  get errorMessage() {
    return (
      this.getAttribute("error-message") ||
      "An error occurred. Please try again."
    );
  }

  async handleSubmit(e) {
    e.preventDefault();

    const emailInput = this.form.querySelector('input[type="email"]');
    const messageContainer = this.form.querySelector(
      "[data-newsletter-message]",
    );
    const formFields = this.form.querySelector("[data-newsletter-form-fields]");
    const submitButton = this.form.querySelector('button[type="submit"]');

    if (!emailInput || !submitButton) return;

    const email = emailInput.value.trim();
    if (!email) return;

    const originalButtonText = submitButton.textContent;
    submitButton.disabled = true;
    // submitButton.textContent = '...';

    const localeInput = this.form.querySelector('input[name="_locale"]');
    const phoneInput = this.form.querySelector('input[name="phone"]');
    const payload = { email: email };
    if (this.source) {
      payload.source = this.source;
    }
    if (localeInput && localeInput.value) {
      payload._locale = localeInput.value;
    }
    if (phoneInput && phoneInput.value.trim()) {
      const phonePlugin = phoneInput.intlTelInputInstance;
      payload.phone =
        phonePlugin && typeof phonePlugin.getNumber === "function"
          ? phonePlugin.getNumber()
          : phoneInput.value.trim();
    }

    try {
      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (
        response.ok &&
        data.success === true &&
        data.conversion_confirmed === true
      ) {
        if (formFields) formFields.style.display = "none";
        if (messageContainer) {
          messageContainer.innerHTML =
            '<p class="alert alert--success alert--center">' +
            this.successMessage +
            "</p>";
          messageContainer.style.display = "block";
        }
        this.dispatchEvent(
          new CustomEvent("newsletter:subscribed", {
            bubbles: true,
            detail: {
              source: this.source || "custom-newsletter",
              endpoint: this.endpoint,
              conversionConfirmed: true,
            },
          }),
        );
      } else {
        throw new Error(data.message || this.errorMessage);
      }
    } catch (error) {
      if (messageContainer) {
        messageContainer.innerHTML =
          '<p class="alert alert--error alert--center">' +
          error.message +
          "</p>";
        messageContainer.style.display = "block";
      }
      submitButton.disabled = false;
      submitButton.textContent = originalButtonText;
      this.dispatchEvent(
        new CustomEvent("newsletter:error", {
          bubbles: true,
          detail: { error: error.message },
        }),
      );
    }
  }
}

if (!customElements.get("newsletter-form")) {
  customElements.define("newsletter-form", NewsletterForm);
}
